// export function MilestonesTab() {
//   return (
//     <div className="section-container">
//       <div className="section-card">
//         <h2 className="section-title">Project Milestones</h2>
//         <p className="section-text">Project milestones and key achievements will be displayed here.</p>
//       </div>
//     </div>
//   );
// }


import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { CheckCircle2, Circle, Clock, Calendar, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const getStatusIcon = (status) => {
	switch (status) {
		case "completed":
			return <CheckCircle2 className="h-5 w-5 text-green-600" />;
		case "in-progress":
			return <Clock className="h-5 w-5 text-blue-600" />;
		case "upcoming":
			return <Circle className="h-5 w-5 text-gray-400" />;
		default:
			return <Circle className="h-5 w-5 text-gray-400" />;
	}
};

const getStatusBadge = (status) => {
	switch (status) {
		case "completed":
			return (
				<Badge className="bg-green-100 text-green-800 hover:bg-green-100">
					Completed
				</Badge>
			);
		case "in-progress":
			return (
				<Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
					In Progress
				</Badge>
			);
		case "upcoming":
			return <Badge variant="outline">Upcoming</Badge>;
		default:
			return <Badge variant="outline">Upcoming</Badge>;
	}
};

export function MilestonesTab() {
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchMilestones = async () => {
      try {
        const milestonesQuery = query(collection(db, 'milestones'), orderBy('createdAt', 'asc'));
        const querySnapshot = await getDocs(milestonesQuery);
        
        const milestonesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setMilestones(milestonesData);
      } catch (err) {
        console.error('Error fetching milestones:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMilestones();
  }, []);

  if (loading) return <div className="text-white text-center py-8">Loading milestones...</div>;

	const completedCount = milestones.filter((m) => m.status === "completed").length;
	const inProgressCount = milestones.filter((m) => m.status === "in-progress").length;
	const upcomingCount = milestones.filter((m) => m.status === "upcoming" || m.status === "planned").length;

  return (
    <div className="space-y-6">
  
        <div className="section-container">
          <div className="section-card">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="section-title">Project Milestones</h1>
                <p className="text-muted-foreground mt-1">
                  Track key milestones in the Miner's Park Master Plan development
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {completedCount} of {milestones.length} Complete
                </Badge>
                {user && (
                  <Button 
                    onClick={() => navigate('/manage-milestones')}
                    className="bg-primary-500 hover:bg-primary-600 text-white flex items-center gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Manage
                  </Button>
                )}
              </div>
            </div>

            <div className="relative mt-4 rounded-lg overflow-hidden">
              <img src="https://www.ourtasmania.com.au/launceston/images/Beaconsfield-Mine2.jpg" alt="Nature banner for community update" className="w-full h-40 object-cover" />
            </div>
          </div>
        </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Completed Milestones</CardDescription>
            <CardTitle className="text-3xl text-green-600">
              {completedCount}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Successfully achieved
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>In Progress</CardDescription>
            <CardTitle className="text-3xl text-blue-600">
              {inProgressCount}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Currently underway
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Upcoming</CardDescription>
            <CardTitle className="text-3xl text-gray-600">
              {upcomingCount}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Planned ahead</p>
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Master Plan Timeline</CardTitle>
          <CardDescription>
            Detailed progression of project milestones from initiation to
            implementation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-border" />

            <div className="space-y-6">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative pl-12">
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-1">
                    {getStatusIcon(milestone.status)}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div>
                        <h3 className="font-medium">{milestone.title}</h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Calendar className="h-3 w-3" />
                          {milestone.date}
                        </p>
                      </div>
                      {getStatusBadge(milestone.status)}
                    </div>

                    <p className="text-sm text-muted-foreground">
                      {milestone.description}
                    </p>

                    {milestone.details && milestone.details.length > 0 && (
                      <div className="mt-2 p-3 bg-muted/30 rounded-lg">
                        <p className="text-sm font-medium mb-2">
                          Key Activities:
                        </p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {milestone.details.map(
                            (detail, detailIndex) => (
                              <li
                                key={detailIndex}
                                className="flex items-start gap-2"
                              >
                                <span className="text-blue-600 mt-1">•</span>
                                <span>{detail}</span>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
